import { 
  collection, 
  query, 
  getDocs,
  getDoc,
  doc,
  where,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { CHAINS, getChainById, getChainForRequest, getChainIdDecimalsForKey, getLogicalChainKey, normalizeChainId } from '../config/chains';
import { getRequestApplication } from '../config/applications';

const REQUESTS_COLLECTION = 'requests';
const AUDITOR_GRANTS_COLLECTION = 'auditorRequestGrants';

/**
 * Normalize an Ethereum address for comparison
 * - Adds 0x prefix if missing
 * - Converts to lowercase
 * - Trims whitespace
 */
const normalizeAddress = (address) => {
  if (!address) return null;
  let normalized = address.trim();
  if (!normalized.startsWith('0x')) {
    normalized = '0x' + normalized;
  }
  return normalized.toLowerCase();
};

/**
 * Map chain abbreviations to chain IDs
 */
const getChainIdFromAbbreviation = (abbreviation) => {
  const mapping = {
    'eth': 'ethereum',
    'ethereum': 'ethereum',
    'arb': 'arbitrum',
    'arbitrum': 'arbitrum',
    'base': 'base',
    'arc': 'arc',
    'stable': 'stable',
    'tempo': 'tempo',
    'hyperevm': 'hyperevm',
    'hype': 'hyperevm',
    'monad': 'monad',
    'mon': 'monad',
    'plasma': 'plasma',
    'noble': 'noble',
    'stellar': 'stellar',
    'xlm': 'stellar',
    'solana': 'solana',
    'sol': 'solana',
    'robinhood': 'robinhood',
    'rh': 'robinhood',
    'bnb': 'bnb',
    'bsc': 'bnb'
  };
  return mapping[abbreviation?.toLowerCase()] || abbreviation?.toLowerCase();
};

/**
 * Map a token filter (any casing) to the canonical symbol stored in transfer
 * requests (e.g. lbcoreUSDG, PathUSD). Firestore equality is case-sensitive.
 */
const getCanonicalTokenSymbol = (symbol) => {
  if (!symbol) return symbol;
  const upper = String(symbol).toUpperCase().trim();
  for (const chain of Object.values(CHAINS)) {
    for (const token of Object.values(chain.tokens || {})) {
      if (token?.symbol && String(token.symbol).toUpperCase() === upper) {
        return token.symbol;
      }
    }
  }
  return String(symbol).trim();
};

/**
 * Check if a request matches an access control rule
 * Access control format: {chain, token, application, userAddress} or [chain, token, application, userAddress]
 * "-" means "all" for that category
 */
const matchesAccessControl = (request, accessControl) => {
  // Handle both object and array formats
  let chainFilter, tokenFilter, appFilter, userFilter;
  
  if (Array.isArray(accessControl)) {
    [chainFilter, tokenFilter, appFilter, userFilter] = accessControl;
  } else {
    // Object format
    chainFilter = accessControl.chain;
    tokenFilter = accessControl.token;
    appFilter = accessControl.application;
    userFilter = accessControl.userAddress;
  }
  
  // Check chain
  // chainId is stored as numeric in DB, but we can also check chainKey if available
  if (chainFilter && chainFilter !== '-') {
    const normalizedChainFilter = getChainIdFromAbbreviation(chainFilter);
    const chain = getChainForRequest(request);
    
    // Check by chain ID match
    let chainMatches = false;
    if (chain && chain.id === normalizedChainFilter) {
      chainMatches = true;
    } else if (request.chainKey && request.chainKey.toLowerCase() === normalizedChainFilter) {
      // Fallback: check chainKey field if it exists
      chainMatches = true;
    }
    
    if (!chainMatches) {
      return false;
    }
  }
  
  // Check token
  if (tokenFilter && tokenFilter !== '-') {
    const requestToken = request.details?.token || '';
    if (requestToken.toUpperCase() !== tokenFilter.toUpperCase()) {
      return false;
    }
  }
  
  // Check application (DB field: applicationName; legacy: application / app)
  if (appFilter && appFilter !== '-') {
    const requestApp = getRequestApplication(request);
    if (requestApp.toLowerCase() !== appFilter.toLowerCase()) {
      return false;
    }
  }
  
  // Check user address
  // CRITICAL: For sender records, only check sender. For receiver records, only check receiver.
  if (userFilter && userFilter !== '-') {
    const requestUser = request.userAddress?.toLowerCase() || '';
    const recipientUser = request.details?.recipient?.toLowerCase() || '';
    const filterUser = userFilter.toLowerCase();
    
    // Determine record type
    const txType = request.type?.toLowerCase() || '';
    const isReceiverRecord = txType === 'received' || txType === 'receive';
    const isSenderRecord = txType === 'send' || txType === 'transfer' || txType === 'sent';
    
    if (isReceiverRecord) {
      // For receiver records: only check if receiver matches
      if (recipientUser !== filterUser) {
        return false;
      }
    } else if (isSenderRecord) {
      // For sender records: only check if sender matches
      if (requestUser !== filterUser) {
        return false;
      }
    } else {
      // Type unclear: check both (fallback)
      if (requestUser !== filterUser && recipientUser !== filterUser) {
        return false;
      }
    }
  }
  
  return true;
};

/**
 * Check if a request matches any of the auditor's access controls
 */
const hasAccess = (request, accessControls) => {
  if (!accessControls || !Array.isArray(accessControls) || accessControls.length === 0) {
    // No access controls means no access
    return false;
  }
  
  return accessControls.some(control => matchesAccessControl(request, control));
};

/**
 * Merge per-auditor encrypted randomness onto request rows.
 * @param {Array} requests
 * @param {string|string[]|null} auditorGrantIds - Parent doc id(s) under
 *   `auditorRequestGrants/{id}/requests/{requestId}`. Backend may file grants
 *   under Firebase UID, email, or the auditors collection doc id — try all
 *   candidates (same idea as kv `extraAuditorIds`).
 */
const mergeAuditorRandomnessGrants = async (requests, auditorGrantIds) => {
  const candidateIds = [
    ...new Set(
      (Array.isArray(auditorGrantIds) ? auditorGrantIds : [auditorGrantIds]).filter(
        (id) => typeof id === 'string' && id.length > 0
      )
    ),
  ];
  if (!candidateIds.length || !requests.length) {
    return requests;
  }

  const grantReads = requests.map(async (request) => {
    for (const auditorGrantId of candidateIds) {
      try {
        const grantRef = doc(
          db,
          AUDITOR_GRANTS_COLLECTION,
          auditorGrantId,
          'requests',
          request.id
        );
        const grantSnap = await getDoc(grantRef);
        if (!grantSnap.exists()) {
          continue;
        }
        const grant = grantSnap.data();
        return {
          ...request,
          encryptedSenderRandomness: grant.encryptedSenderRandomness,
          encryptedReceiverRandomness: grant.encryptedReceiverRandomness,
          queuedEncryptedSenderRandomness: grant.queuedEncryptedSenderRandomness,
          queuedEncryptedReceiverRandomness: grant.queuedEncryptedReceiverRandomness,
          encryptedSenderRandomnessId: grant.encryptedSenderRandomnessId,
          encryptedReceiverRandomnessId: grant.encryptedReceiverRandomnessId,
          queuedEncryptedSenderRandomnessId: grant.queuedEncryptedSenderRandomnessId,
          queuedEncryptedReceiverRandomnessId: grant.queuedEncryptedReceiverRandomnessId,
          randomnessGrantId: grantSnap.id,
          randomnessGrantAuditorId: auditorGrantId,
        };
      } catch (error) {
        // Permission-denied or network: try the next identity before giving up.
        console.warn(
          'Unable to read auditor randomness grant for request:',
          request.id,
          'under',
          auditorGrantId,
          error
        );
      }
    }
    console.warn(
      'No auditor randomness grant found for request:',
      request.id,
      'tried auditor ids:',
      candidateIds
    );
    return request;
  });

  return Promise.all(grantReads);
};

/**
 * Extract user addresses from access controls
 * Returns array of allowed user addresses, or null if all users are allowed
 */
const extractAllowedUserAddresses = (accessControls) => {
  if (!accessControls || !Array.isArray(accessControls) || accessControls.length === 0) {
    return null; // No access controls means no access
  }
  
  const userAddresses = new Set();
  let hasWildcard = false;
  
  accessControls.forEach(control => {
    let userFilter;
    if (Array.isArray(control)) {
      userFilter = control[3]; // [chain, token, application, userAddress]
    } else {
      userFilter = control.userAddress;
    }
    
    if (!userFilter || userFilter === '-') {
      hasWildcard = true; // "-" means all users allowed
    } else {
      userAddresses.add(userFilter.toLowerCase());
    }
  });
  
  // If any control allows all users, return null (no restriction)
  if (hasWildcard) {
    return null;
  }
  
  // Return array of allowed addresses, or empty array if none specified
  return userAddresses.size > 0 ? Array.from(userAddresses) : null;
};

/**
 * Extract allowed chain IDs (chainIdDecimal) from access controls.
 * Returns array of chainIdDecimal numbers, or null if any control allows all chains ("-").
 * Used to enforce chain restriction at query time so we never fetch unauthorized chain data.
 */
const extractAllowedChainIds = (accessControls) => {
  if (!accessControls || !Array.isArray(accessControls) || accessControls.length === 0) {
    return null;
  }
  const chainIdDecimals = new Set();
  let hasWildcard = false;
  accessControls.forEach(control => {
    let chainFilter;
    if (Array.isArray(control)) {
      chainFilter = control[0]; // [chain, token, application, userAddress]
    } else {
      chainFilter = control.chain;
    }
    if (!chainFilter || chainFilter === '-') {
      hasWildcard = true;
    } else {
      const chainKey = getChainIdFromAbbreviation(chainFilter);
      getChainIdDecimalsForKey(chainKey).forEach((id) => chainIdDecimals.add(id));
    }
  });
  if (hasWildcard) return null;
  return chainIdDecimals.size > 0 ? Array.from(chainIdDecimals) : null;
};

/**
 * Extract allowed token symbols from access controls (canonical casing, e.g. lbcoreUSDG).
 * Returns array of token strings, or null if any control allows all tokens ("-").
 * Used to validate request filters and (where possible) restrict queries.
 * Comparisons elsewhere should still use case-insensitive matching.
 */
const extractAllowedTokens = (accessControls) => {
  if (!accessControls || !Array.isArray(accessControls) || accessControls.length === 0) {
    return null;
  }
  const tokens = new Map(); // upper -> canonical
  let hasWildcard = false;
  accessControls.forEach(control => {
    let tokenFilter;
    if (Array.isArray(control)) {
      tokenFilter = control[1]; // [chain, token, application, userAddress]
    } else {
      tokenFilter = control.token;
    }
    if (!tokenFilter || tokenFilter === '-') {
      hasWildcard = true;
    } else {
      const canonical = getCanonicalTokenSymbol(tokenFilter);
      tokens.set(String(canonical).toUpperCase(), canonical);
    }
  });
  if (hasWildcard) return null;
  return tokens.size > 0 ? Array.from(tokens.values()) : null;
};

/**
 * Get allowed chain keys (e.g. ['ethereum', 'arbitrum']) for UI filtering.
 * Returns null if auditor can access all chains.
 */
export const getAllowedChainKeys = (accessControls) => {
  if (!accessControls || !Array.isArray(accessControls) || accessControls.length === 0) {
    return null;
  }
  const keys = new Set();
  let hasWildcard = false;
  accessControls.forEach(control => {
    let chainFilter;
    if (Array.isArray(control)) {
      chainFilter = control[0];
    } else {
      chainFilter = control.chain;
    }
    if (!chainFilter || chainFilter === '-') {
      hasWildcard = true;
    } else {
      const chainKey = getChainIdFromAbbreviation(chainFilter);
      if (CHAINS[chainKey]) keys.add(chainKey);
    }
  });
  return hasWildcard ? null : (keys.size > 0 ? Array.from(keys) : null);
};

/**
 * Get allowed token symbols (canonical casing) for UI filtering.
 * Returns null if auditor can access all tokens.
 */
export const getAllowedTokenSymbols = (accessControls) => {
  return extractAllowedTokens(accessControls);
};

/**
 * Get allowed application names for UI filtering.
 * Returns null if auditor can access all applications (or has a "-" wildcard).
 */
export const getAllowedApplications = (accessControls) => {
  if (!accessControls || !Array.isArray(accessControls) || accessControls.length === 0) {
    return null;
  }
  const apps = new Set();
  let hasWildcard = false;
  accessControls.forEach((control) => {
    let appFilter;
    if (Array.isArray(control)) {
      appFilter = control[2];
    } else {
      appFilter = control.application;
    }
    if (!appFilter || appFilter === '-') {
      hasWildcard = true;
    } else {
      apps.add(appFilter);
    }
  });
  return hasWildcard ? null : (apps.size > 0 ? Array.from(apps) : null);
};

/**
 * Get all unique user addresses from transfer requests (filtered by auditor access)
 */
export const getAllUserAddresses = async (auditorAccessControls = null) => {
  try {
    // Extract allowed user addresses and chains from access controls
    const allowedUserAddresses = extractAllowedUserAddresses(auditorAccessControls);
    const allowedChainIds = extractAllowedChainIds(auditorAccessControls);
    
    // If access controls specify specific user addresses, only return those
    if (allowedUserAddresses && allowedUserAddresses.length > 0) {
      return allowedUserAddresses.sort();
    }
    
    // Build query - restrict to allowed chains at query time when access controls restrict chain
    const baseConstraints = [where('schemaVersion', '==', 2), where('operationKind', '==', 2)]; // 2 = Transfer
    if (allowedChainIds && allowedChainIds.length > 0) {
      baseConstraints.push(where('chainId', 'in', allowedChainIds.slice(0, 30)));
    }
    let q = query(
      collection(db, REQUESTS_COLLECTION),
      ...baseConstraints,
      orderBy('createdAt', 'desc'),
      limit(10000) // Firestore limit
    );

    let querySnapshot;
    try {
      querySnapshot = await getDocs(q);
    } catch (error) {
      // Fallback without orderBy if index missing
      if (error.code === 'failed-precondition' && error.message?.includes('index')) {
        console.warn('Firestore index missing for getAllUserAddresses. Using simpler query. See FIRESTORE_INDEXES.md for details.');
        q = query(
          collection(db, REQUESTS_COLLECTION),
          ...baseConstraints,
          limit(10000)
        );
        querySnapshot = await getDocs(q);
      } else {
        throw error;
      }
    }
    const userAddresses = new Set();

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const request = { id: doc.id, ...data };
      
      // Apply access controls if provided (token, application, etc.)
      if (auditorAccessControls && !hasAccess(request, auditorAccessControls)) {
        return;
      }
      
      if (data.userAddress) {
        userAddresses.add(data.userAddress.toLowerCase());
      }
      // Also check for recipient in transfer details
      if (data.details && data.details.recipient) {
        userAddresses.add(data.details.recipient.toLowerCase());
      }
    });

    return Array.from(userAddresses).sort();
  } catch (error) {
    console.error('Error getting user addresses:', error);
    throw error;
  }
};

/**
 * Get transfer requests with filters and access control
 * @param {Object} filters - Filter object with chain, token, application, userAddress
 * @param {Array} auditorAccessControls - Array of access control rules for the auditor
 * @param {string|string[]|null} auditorGrantIds - auditorRequestGrants parent id(s) to merge randomness from
 */
export const getTransferRequests = async (filters = {}, auditorAccessControls = null, auditorGrantIds = null) => {
  try {
    const { chain, token, application, userAddress: filterUserAddress } = filters;
    
    // SECURITY: Extract allowed user addresses, chains, and tokens from access controls FIRST
    // This ensures we only query data the auditor is allowed to access (enforced at query time)
    const allowedUserAddresses = extractAllowedUserAddresses(auditorAccessControls);
    const allowedChainIds = extractAllowedChainIds(auditorAccessControls);
    const allowedTokens = extractAllowedTokens(auditorAccessControls);
    
    console.log('Access control extraction:', {
      hasAccessControls: !!auditorAccessControls,
      accessControlsLength: auditorAccessControls?.length || 0,
      allowedUserAddresses: allowedUserAddresses,
      allowedUserAddressesLength: allowedUserAddresses?.length || 0,
      allowedChainIds: allowedChainIds,
      allowedTokens: allowedTokens
    });
    
    // SECURITY: Enforce chain at query time - never fetch data for chains the auditor cannot access
    let effectiveChainIds = null; // null = no chain constraint, [] = no access
    // Resolve the requested chain filter to a decimal chainId. The filter value may be
    // a configured chain slug (e.g. "ethereum") or a raw chainId for chains not in the
    // static config (e.g. "97" / "0x61" for BNB Smart Chain Testnet).
    const requestedChainId = chain != null && chain !== ''
      ? (CHAINS[chain]?.chainIdDecimal ?? normalizeChainId(chain))
      : null;

    if (allowedChainIds && allowedChainIds.length > 0) {
      if (chain) {
        if (requestedChainId != null && allowedChainIds.includes(requestedChainId)) {
          effectiveChainIds = [requestedChainId];
        } else {
          // Auditor requested a chain they are not allowed to access - return empty
          console.warn('SECURITY: Requested chain not in access controls:', chain);
          return [];
        }
      } else {
        effectiveChainIds = allowedChainIds.slice(0, 30); // Firestore 'in' limit is 30
      }
    } else if (chain) {
      // No access control chain restriction; use user filter
      if (requestedChainId != null) {
        effectiveChainIds = [requestedChainId];
      }
    }
    
    // SECURITY: Enforce token at query time - never fetch documents for tokens the auditor cannot access.
    // allowedTokens / filters use canonical casing (lbcoreUSDG, PathUSD, …) for Firestore equality.
    let effectiveTokens = null; // null = no token constraint, [] = no access
    if (allowedTokens && allowedTokens.length > 0) {
      const allowedUpper = new Set(allowedTokens.map((t) => String(t).toUpperCase()));
      if (token) {
        const requestedUpper = token.toUpperCase().trim();
        if (allowedUpper.has(requestedUpper)) {
          effectiveTokens = [getCanonicalTokenSymbol(token)];
        } else {
          console.warn('SECURITY: Requested token not in access controls:', token);
          return [];
        }
      } else {
        effectiveTokens = allowedTokens.slice(0, 30); // Firestore 'in' limit is 30
      }
    } else if (token) {
      // No access-control token restriction; still query by the user-selected token filter.
      effectiveTokens = [getCanonicalTokenSymbol(token)];
    }
    // When no token access restriction and no filter, we don't add token to query

    // SECURITY: Determine which user addresses we can query
    // If access controls restrict to specific addresses, ONLY query those
    // If user provides a filter, it must be within allowed addresses
    let userAddressesToQuery = [];
    
    if (allowedUserAddresses && allowedUserAddresses.length > 0) {
      // Access controls restrict to specific addresses
      if (filterUserAddress) {
        const normalizedFilter = normalizeAddress(filterUserAddress);
        // Only include filter address if it's in the allowed list
        if (allowedUserAddresses.includes(normalizedFilter)) {
          userAddressesToQuery = [normalizedFilter];
        } else {
          // User tried to filter by an address they don't have access to - return empty
          console.warn('User attempted to filter by address not in access controls');
          return [];
        }
      } else {
        // No filter, but access controls restrict - query all allowed addresses
        userAddressesToQuery = allowedUserAddresses.map(addr => normalizeAddress(addr));
      }
    } else if (filterUserAddress) {
      // No access control restrictions, use filter if provided
      userAddressesToQuery = [normalizeAddress(filterUserAddress)];
    }
    // If no restrictions and no filter, we'll query all (but still apply access controls to results)
    
    // Build base query
    const constraints = [where('schemaVersion', '==', 2), where('operationKind', '==', 2)]; // 2 = Transfer
    
    // SECURITY: Add chain constraint from effectiveChainIds (derived from access controls + filter)
    if (effectiveChainIds && effectiveChainIds.length > 0) {
      constraints.push(where('chainId', 'in', effectiveChainIds));
    }
    // SECURITY: Add token constraint when access controls restrict token - never fetch unauthorized token data
    if (effectiveTokens && effectiveTokens.length > 0) {
      constraints.push(where('details.token', 'in', effectiveTokens));
    }
    // Application is still filtered in memory (no index/field assumption)
    
    // Since each transfer creates two records (one for sender, one for receiver),
    // we need to query both userAddress (for sender records) and details.recipient (for receiver records)
    // SECURITY: Only query addresses the auditor has access to
    console.log('Building queries for addresses:', userAddressesToQuery, 'accessControls:', !!auditorAccessControls);
    
    let senderQueries = [];
    let recipientQueries = [];
    
    if (userAddressesToQuery.length > 0) {
      // SECURITY: Verify all addresses in userAddressesToQuery are actually authorized
      if (allowedUserAddresses && allowedUserAddresses.length > 0) {
        const unauthorizedQueries = userAddressesToQuery.filter(addr => {
          const normalized = normalizeAddress(addr);
          return !allowedUserAddresses.includes(normalized);
        });
        if (unauthorizedQueries.length > 0) {
          console.error('SECURITY ERROR: Attempted to query for unauthorized addresses:', unauthorizedQueries);
          throw new Error('Security violation: Cannot query for unauthorized addresses');
        }
      }
      
      // Query for each allowed address
      for (const addr of userAddressesToQuery) {
        // Query 1: Records where userAddress matches (gets sender records)
        // SECURITY: Only query for sender records (type="send" or "transfer")
        // This ensures we only get records where the sender is authorized
        const senderConstraints = [
          ...constraints,
          where('userAddress', '==', addr),
          where('type', 'in', ['send', 'transfer', 'Send', 'Transfer', 'sent', 'Sent']) // Only sender records
        ];
        
        senderQueries.push(query(
          collection(db, REQUESTS_COLLECTION),
          ...senderConstraints,
          orderBy('createdAt', 'desc'),
          limit(1000)
        ));
        
        // Query 2: Records where details.recipient matches (gets receiver records)
        // SECURITY: Only query for receiver records (type="received" or "receive")
        // This prevents fetching sender records where sender is not authorized
        const recipientConstraints = [
          ...constraints,
          where('details.recipient', '==', addr),
          where('type', 'in', ['received', 'receive', 'Received', 'Receive']) // Only receiver records
        ];
        
        recipientQueries.push(query(
          collection(db, REQUESTS_COLLECTION),
          ...recipientConstraints,
          orderBy('createdAt', 'desc'),
          limit(1000)
        ));
      }
    } else {
      // No specific addresses to query
      // SECURITY: If access controls restrict by user address, we MUST NOT query all records
      // This would be a security violation - return empty instead
      if (allowedUserAddresses && allowedUserAddresses.length > 0) {
        console.error('SECURITY ERROR: allowedUserAddresses exists but userAddressesToQuery is empty!');
        console.error('This should never happen - returning empty result for security');
        return [];
      }
      
      // Only query all if there are NO access control restrictions on user addresses
      // This is safe because we'll still apply access controls in memory
      senderQueries.push(query(
        collection(db, REQUESTS_COLLECTION),
        ...constraints,
        limit(5000) // Increased limit since we'll filter in memory
      ));
    }
    
    // Execute all queries and collect results
    const allSenderDocs = [];
    const allRecipientDocs = [];
    
    try {
      // Execute all sender queries
      for (let i = 0; i < senderQueries.length; i++) {
        const q = senderQueries[i];
        const addr = userAddressesToQuery[i];
        try {
          const snapshot = await getDocs(q);
          allSenderDocs.push(...snapshot.docs);
        } catch (error) {
          if (error.code === 'failed-precondition' && error.message?.includes('index')) {
            // Fallback without orderBy but WITH type filter for security
            console.warn('Sender query index missing, trying without orderBy for address:', addr);
            const fallbackConstraints = [
              where('schemaVersion', '==', 2),
              where('operationKind', '==', 2),
              where('type', 'in', ['send', 'transfer', 'Send', 'Transfer', 'sent', 'Sent']) // SECURITY: Still filter by type
            ];
            if (effectiveChainIds && effectiveChainIds.length > 0) {
              fallbackConstraints.push(where('chainId', 'in', effectiveChainIds));
            }
            if (effectiveTokens && effectiveTokens.length > 0) {
              fallbackConstraints.push(where('details.token', 'in', effectiveTokens));
            }
            if (addr) {
              fallbackConstraints.push(where('userAddress', '==', addr));
              const fallbackQuery = query(
                collection(db, REQUESTS_COLLECTION),
                ...fallbackConstraints,
                limit(1000)
              );
              const fallbackSnapshot = await getDocs(fallbackQuery);
              allSenderDocs.push(...fallbackSnapshot.docs);
            }
          } else {
            console.error('Sender query failed:', error);
            // If query fails for other reasons, don't return unauthorized data
            // The in-memory filtering will handle any records that slip through
          }
        }
      }
      console.log('Total sender documents:', allSenderDocs.length);
      
      // Execute all recipient queries
      for (let i = 0; i < recipientQueries.length; i++) {
        const q = recipientQueries[i];
        const addr = userAddressesToQuery[i];
        try {
          const snapshot = await getDocs(q);
          allRecipientDocs.push(...snapshot.docs);
        } catch (error) {
          if (error.code === 'failed-precondition' && error.message?.includes('index')) {
            // Fallback without orderBy but WITH type filter for security
            console.warn('Recipient query index missing, trying without orderBy for address:', addr);
            const fallbackConstraints = [
              where('schemaVersion', '==', 2),
              where('operationKind', '==', 2),
              where('type', 'in', ['received', 'receive', 'Received', 'Receive']) // SECURITY: Still filter by type
            ];
            if (effectiveChainIds && effectiveChainIds.length > 0) {
              fallbackConstraints.push(where('chainId', 'in', effectiveChainIds));
            }
            if (effectiveTokens && effectiveTokens.length > 0) {
              fallbackConstraints.push(where('details.token', 'in', effectiveTokens));
            }
            if (addr) {
              fallbackConstraints.push(where('details.recipient', '==', addr));
              const fallbackQuery = query(
                collection(db, REQUESTS_COLLECTION),
                ...fallbackConstraints,
                limit(1000)
              );
              const fallbackSnapshot = await getDocs(fallbackQuery);
              allRecipientDocs.push(...fallbackSnapshot.docs);
            }
          } else {
            console.error('Recipient query failed:', error);
            // If query fails for other reasons, don't return unauthorized data
            // The in-memory filtering will handle any records that slip through
          }
        }
      }
      console.log('Total recipient documents:', allRecipientDocs.length);
    } catch (error) {
      console.error('Error executing queries:', error);
      throw error;
    }

    // SECURITY: Pre-filter recipient documents to remove any sender records that slipped through
    // This is a critical security check - recipient queries should ONLY return receiver records
    if (allowedUserAddresses && allowedUserAddresses.length > 0) {
      const filteredRecipientDocs = [];
      for (const doc of allRecipientDocs) {
        const data = doc.data();
        const txType = data.type?.toLowerCase() || '';
        const isReceiverRecord = txType === 'received' || txType === 'receive';
        
        // If this is NOT a receiver record, it shouldn't be in recipient query results
        // This could happen if the type filter in the query didn't work
        if (!isReceiverRecord) {
          const senderAddr = normalizeAddress(data.userAddress);
          const recipientAddr = normalizeAddress(data.details?.recipient);
          // Check if sender is authorized - if not, this is an unauthorized sender record
          const senderIsAuthorized = allowedUserAddresses.some(addr => addr === senderAddr);
          
          if (!senderIsAuthorized) {
            console.warn('SECURITY: Found unauthorized sender record in recipient query results:', doc.id, {
              type: txType,
              sender: senderAddr,
              recipient: recipientAddr,
              allowedAddresses: allowedUserAddresses
            });
            continue; // Skip this document - it's an unauthorized sender record
          }
        }
        filteredRecipientDocs.push(doc);
      }
      console.log(`Pre-filtered recipient docs: ${allRecipientDocs.length} -> ${filteredRecipientDocs.length} (removed ${allRecipientDocs.length - filteredRecipientDocs.length} unauthorized sender records)`);
      allRecipientDocs.length = 0;
      allRecipientDocs.push(...filteredRecipientDocs);
    }

    const requests = [];
    const processedRequests = new Map(); // Track processed requests by ID to handle duplicates properly
    
    // Determine the user address we're viewing (from filter or access controls)
    const viewingUserAddress = userAddressesToQuery.length === 1 ? userAddressesToQuery[0] : 
                               (filterUserAddress ? normalizeAddress(filterUserAddress) : null);
    
    console.log('Processing transactions:', {
      senderDocs: allSenderDocs.length,
      recipientDocs: allRecipientDocs.length,
      viewingUserAddress,
      hasChainFilter: !!chain,
      hasTokenFilter: !!token,
      hasApplicationFilter: !!application,
      accessControlsApplied: !!auditorAccessControls,
      allowedUserAddresses: allowedUserAddresses || 'ALL (no restrictions)',
      userAddressesToQuery: userAddressesToQuery
    });
    
    // Helper function to process a document with SECURITY checks
    const processDocument = (doc, defaultRole = 'sender', source = 'unknown') => {
      const data = doc.data();
      
      // Determine role based on the type field and viewing user
      const txType = data.type?.toLowerCase() || '';
      let role = defaultRole;
      
      if (txType === 'received' || txType === 'receive') {
        role = 'recipient';
      } else if (txType === 'transfer' || txType === 'send' || txType === 'sent') {
        role = 'sender';
      }
      
      // CRITICAL SECURITY: If we have allowed addresses, verify authorization IMMEDIATELY
      // This prevents any unauthorized records from being processed further
      if (allowedUserAddresses && allowedUserAddresses.length > 0) {
        const requestUser = data.userAddress?.toLowerCase() || '';
        const recipientUser = data.details?.recipient?.toLowerCase() || '';
        const normalizedRequestUser = normalizeAddress(requestUser);
        const normalizedRecipientUser = normalizeAddress(recipientUser);
        
        // For sender records (type="send"/"transfer"/"sent"), sender MUST be authorized
        if (txType === 'send' || txType === 'transfer' || txType === 'sent') {
          const senderIsAuthorized = allowedUserAddresses.some(addr => addr === normalizedRequestUser);
          if (!senderIsAuthorized) {
            console.error(`[${source}] SECURITY BLOCK: Unauthorized sender record blocked immediately:`, doc.id, {
              type: txType,
              sender: normalizedRequestUser,
              recipient: normalizedRecipientUser,
              allowedAddresses: allowedUserAddresses,
              source
            });
            return null; // BLOCK immediately - sender is not authorized
          }
        }
        
        // For receiver records (type="received"/"receive"), receiver MUST be authorized
        if (txType === 'received' || txType === 'receive') {
          const receiverIsAuthorized = allowedUserAddresses.some(addr => addr === normalizedRecipientUser);
          if (!receiverIsAuthorized) {
            console.error(`[${source}] SECURITY BLOCK: Unauthorized receiver record blocked immediately:`, doc.id, {
              type: txType,
              sender: normalizedRequestUser,
              recipient: normalizedRecipientUser,
              allowedAddresses: allowedUserAddresses,
              source
            });
            return null; // BLOCK immediately - receiver is not authorized
          }
        }
      }
      
      // If document already processed, check if we need to update the role
      if (processedRequests.has(doc.id)) {
        const existingRequest = processedRequests.get(doc.id);
        // If this is from recipient query and the type is "received", prefer recipient role
        if (source === 'recipient-query' && txType === 'received') {
          existingRequest.role = 'recipient';
          console.log(`[${source}] Updating role to recipient for duplicate:`, doc.id);
        }
        return null; // Already processed, skip
      }
      
      const request = {
        id: doc.id,
        ...data,
        role: role
      };
      
      // Filter out invalid transactions (sender and receiver are the same)
      // BUT: For receiver records, userAddress and recipient are both the receiver address, which is valid
      const requestUser = request.userAddress?.toLowerCase() || '';
      const recipientUser = request.details?.recipient?.toLowerCase() || '';
      // Only skip if this is NOT a receiver record (type="received") and both addresses are the same
      if (requestUser && recipientUser && requestUser === recipientUser && txType !== 'received' && txType !== 'receive') {
        console.log(`[${source}] Skipping same sender/receiver:`, doc.id);
        return null; // Skip transactions where sender and receiver are the same (but not receiver records)
      }
      
      // CRITICAL SECURITY CHECK: Filter by allowed addresses BEFORE other access control checks
      // This ensures sender records are filtered out if sender is not authorized, even if receiver is
      if (allowedUserAddresses && allowedUserAddresses.length > 0) {
        // Normalize the request addresses for comparison
        const normalizedRequestUser = normalizeAddress(requestUser);
        const normalizedRecipientUser = normalizeAddress(recipientUser);
        
        // Determine if this is a receiver record or sender record
        // CRITICAL: Use type field as the primary indicator - it's the most reliable
        const isReceiverRecord = txType === 'received' || txType === 'receive';
        // If type is "send", "transfer", "sent", or "Sent", it's ALWAYS a sender record
        const isSenderRecord = txType === 'send' || txType === 'transfer' || txType === 'sent';
        
        // SECURITY: For sender records, ONLY check sender address, never receiver
        // For receiver records, ONLY check receiver address
        let isAllowed = false;
        
        if (isReceiverRecord) {
          // This is a receiver record: check if receiver is in allowed addresses
          isAllowed = allowedUserAddresses.some(addr => {
            return addr === normalizedRecipientUser;
          });
          if (!isAllowed) {
            console.log(`[${source}] BLOCKED receiver record - receiver not in allowed list:`, doc.id, {
              recipientUser: normalizedRecipientUser,
              allowedAddresses: allowedUserAddresses,
              txType,
              source
            });
            return null;
          }
        } else if (isSenderRecord) {
          // This is a sender record: ONLY check if sender is in allowed addresses
          // CRITICAL: Do NOT check receiver - if sender is not authorized, block the record
          isAllowed = allowedUserAddresses.some(addr => {
            return addr === normalizedRequestUser;
          });
          if (!isAllowed) {
            console.log(`[${source}] BLOCKED sender record - sender not in allowed list:`, doc.id, {
              requestUser: normalizedRequestUser,
              recipientUser: normalizedRecipientUser,
              allowedAddresses: allowedUserAddresses,
              txType,
              source,
              note: 'Sender is not authorized, even though receiver might be'
            });
            return null;
          }
        } else {
          // Type is unclear or missing - determine based on source query
          // SECURITY: Be strict - if we can't determine the type, use source query
          if (source === 'recipient-query') {
            // From recipient query: only allow if receiver is authorized
            // But if userAddress doesn't match recipient, it might be a sender record - block it
            if (normalizedRequestUser && normalizedRecipientUser && normalizedRequestUser !== normalizedRecipientUser) {
              // userAddress != recipient means this is likely a sender record - block it
              console.log(`[${source}] BLOCKED - appears to be sender record from recipient query:`, doc.id, {
                requestUser: normalizedRequestUser,
                recipientUser: normalizedRecipientUser,
                txType,
                source
              });
              return null;
            }
            isAllowed = allowedUserAddresses.some(addr => {
              return addr === normalizedRecipientUser;
            });
          } else if (source === 'sender-query') {
            // From sender query: only allow if sender is authorized
            isAllowed = allowedUserAddresses.some(addr => {
              return addr === normalizedRequestUser;
            });
          } else {
            // Unknown source - be very conservative, block by default
            console.log(`[${source}] BLOCKED - unknown source:`, doc.id, {
              requestUser: normalizedRequestUser,
              recipientUser: normalizedRecipientUser,
              txType,
              source
            });
            return null;
          }
          if (!isAllowed) {
            console.log(`[${source}] BLOCKED record with unclear type - not in allowed list:`, doc.id, {
              requestUser: normalizedRequestUser,
              recipientUser: normalizedRecipientUser,
              allowedAddresses: allowedUserAddresses,
              txType,
              source
            });
            return null;
          }
        }
      }
      
      // SECURITY: Apply access controls - this is still important for chain/token/application filtering
      // Never return data the auditor doesn't have access to
      if (auditorAccessControls && !hasAccess(request, auditorAccessControls)) {
        console.log(`[${source}] Skipping - no access control match:`, doc.id, {
          userAddress: requestUser,
          recipient: recipientUser,
          chain: request.chainId,
          token: request.details?.token
        });
        return null; // Auditor doesn't have access to this transaction
      }
      
      // Apply chain filter (client-side narrowing). Handles chains not in the static
      // config and cosmos chains that share chainId 0 (disambiguated by logical key).
      if (chain) {
        let chainMatches;
        if (CHAINS[chain]) {
          // Known chain slug: match by logical key (e.g. stellar vs noble at chainId 0)
          chainMatches = getLogicalChainKey(request) === chain;
        } else {
          // Raw chainId filter (e.g. "97"): match by normalized decimal chainId
          const filterDecimal = normalizeChainId(chain);
          chainMatches = filterDecimal != null && normalizeChainId(request.chainId) === filterDecimal;
        }
        if (!chainMatches) {
          console.log(`[${source}] Skipping - chain mismatch:`, doc.id, {
            expected: chain,
            actual: request.chainId
          });
          return null;
        }
      }

      // Apply filters (token, application)
      if (token && request.details?.token?.toUpperCase() !== token.toUpperCase()) {
        console.log(`[${source}] Skipping - token mismatch:`, doc.id, {
          expected: token,
          actual: request.details?.token
        });
        return null;
      }
      
      if (application) {
        const requestApp = getRequestApplication(request);
        if (requestApp.toLowerCase() !== application.toLowerCase()) {
          console.log(`[${source}] Skipping - application mismatch:`, doc.id, {
            expected: application,
            actual: requestApp
          });
          return null;
        }
      }
      
      console.log(`[${source}] Accepting transaction:`, doc.id, {
        role,
        type: txType,
        userAddress: requestUser,
        recipient: recipientUser
      });
      
      // Add to processed requests map
      processedRequests.set(doc.id, request);
      return request;
    };
    
    // Process recipient transactions FIRST so they get the correct role
    // (in case a document appears in both queries)
    console.log('Processing', allRecipientDocs.length, 'recipient documents...');
    for (const doc of allRecipientDocs) {
      const docData = doc.data();
      console.log('Processing recipient doc:', doc.id, 'data:', {
        type: docData.type,
        userAddress: docData.userAddress,
        recipient: docData.details?.recipient,
        source: 'recipient-query'
      });
      const request = processDocument(doc, 'recipient', 'recipient-query');
      if (request) {
        console.log('✓ Recipient document ACCEPTED:', doc.id, 'role:', request.role);
        requests.push(request);
      } else {
        console.log('✗ Recipient document REJECTED:', doc.id);
      }
    }
    
    // Process sender transactions
    console.log('Processing', allSenderDocs.length, 'sender documents...');
    for (const doc of allSenderDocs) {
      const docData = doc.data();
      console.log('Processing sender doc:', doc.id, 'data:', {
        type: docData.type,
        userAddress: docData.userAddress,
        recipient: docData.details?.recipient,
        source: 'sender-query'
      });
      const request = processDocument(doc, 'sender', 'sender-query');
      if (request) {
        console.log('✓ Sender document ACCEPTED:', doc.id, 'role:', request.role);
        requests.push(request);
      } else {
        console.log('✗ Sender document REJECTED:', doc.id);
      }
    }
    
    console.log('Final processed transactions:', requests.length, 'after access control filtering');

    const requestsWithGrants = await mergeAuditorRandomnessGrants(requests, auditorGrantIds);

    // Sort by createdAt descending (always sort in memory to ensure consistency)
    return requestsWithGrants.sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
      const bTime = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
      return bTime - aTime;
    });
  } catch (error) {
    console.error('Error getting transfer requests:', error);
    throw error;
  }
};

/**
 * Get all transfer requests for a specific user address (legacy function, kept for compatibility)
 */
export const getUserTransferRequests = async (userAddress, chainId = null, auditorAccessControls = null, auditorGrantIds = null) => {
  const filters = { userAddress };
  if (chainId) {
    // Find chain by chainId
    const chain = getChainById(chainId);
    if (chain) {
      filters.chain = chain.id;
    }
  }
  return getTransferRequests(filters, auditorAccessControls, auditorGrantIds);
};
